import React from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ALL_AGENTS_KEY } from "../issueAnalyticsHelpers";
import { MIN_ISSUES_PER_POINT } from "./stats";

const PALETTE = ["#7C3AED", "#EF4444", "#F59E0B", "#0EA5E9", "#10B981", "#EC4899", "#6366F1"];

function seriesColor(series, index) {
  if (series.key === ALL_AGENTS_KEY) return "#111827";
  return PALETTE[index % PALETTE.length];
}

function fmtWeek(week) {
  const d = new Date(`${week}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return week;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className='bg-white border border-border-subtle rounded-lg shadow-lg p-2 text-caption'>
      <p className='font-semibold text-text-primary mb-1'>Week of {fmtWeek(label)}</p>
      {payload.map((p) => {
        const n = p.payload[`${p.dataKey}__n`];
        return (
          <p key={p.dataKey} style={{ color: p.color }}>
            {p.name}: {p.value == null ? "n/a" : `${(p.value * 100).toFixed(1)}%`}
            <span className='text-text-tertiary'> · {n ?? 0} issues</span>
          </p>
        );
      })}
    </div>
  );
}

// Weekly reject rate (discarded / issues surfaced that week) for the current slice.
export default function TrendChart({ trend, basisLabel, subtitle }) {
  const { series, data } = trend || { series: [], data: [] };
  return (
    <div className='bg-white border border-border-subtle rounded-2xl p-4' data-testid='trend-chart'>
      <div className='flex items-start justify-between gap-2 flex-wrap'>
        <div>
          <h2 className='text-h3 text-text-primary'>Reject rate by week</h2>
          <p className='text-body-xs text-text-secondary'>
            {subtitle || "Discarded ÷ issues per ISO week"}
            {basisLabel ? ` · weeks by ${basisLabel}` : ""}
            {` · weeks with fewer than ${MIN_ISSUES_PER_POINT} issues are left blank`}
          </p>
        </div>
        {series.length > 0 && (
          <div className='flex flex-wrap gap-x-3 gap-y-1'>
            {series.map((s, i) => (
              <span key={s.key} className='flex items-center gap-1 text-caption text-text-secondary'>
                <span className='inline-block w-3 h-0.5' style={{ backgroundColor: seriesColor(s, i) }} />
                {s.title}
              </span>
            ))}
          </div>
        )}
      </div>
      {series.length === 0 || data.length < 2 ? (
        <p className='text-text-tertiary text-body-xs mt-3'>
          {series.length === 0
            ? "No weekly data in this slice (the Platform build must return weekly buckets)."
            : "Only one week in range — widen the time range to see a trend."}
        </p>
      ) : (
        <div className='h-56 mt-3'>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='#EEF0F3' />
              <XAxis dataKey='week' tickFormatter={fmtWeek} tick={{ fontSize: 11 }} stroke='#9CA3AF' />
              <YAxis
                tickFormatter={(v) => `${Math.round(v * 100)}%`}
                domain={[0, (max) => Math.min(1, Math.max(0.1, max || 0))]}
                tick={{ fontSize: 11 }}
                stroke='#9CA3AF'
                width={40}
              />
              <Tooltip content={<TrendTooltip />} />
              {series.map((s, i) => (
                <Line
                  key={s.key}
                  type='monotone'
                  dataKey={s.key}
                  name={s.title}
                  stroke={seriesColor(s, i)}
                  strokeWidth={s.key === ALL_AGENTS_KEY ? 2.5 : 1.5}
                  strokeDasharray={s.key === ALL_AGENTS_KEY ? "4 3" : undefined}
                  dot={{ r: 2 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
